import { useCallback, useState } from "react";

import { useNewPasswordError } from "../../_shared/use-new-password-error";
import { authSignUpInputZodSchema, type AuthSignUpInputType } from "../_schema/validation-schema";

import type { SafeParseError } from "zod";

const DEFAULT_VALUES: AuthSignUpInputType = {
	email: "",
	password: "",
	companyName: "",
	personInChargeName: "",
	tel: "",
	howDoYouKnow: "",
	agree: false,
};

// 初期表示の時点で未入力の項目のエラーを出しておく。入力のたびにエラー枠が増えてフォームが伸びるのを防ぐ
const getInitialValidationErrors = (): SafeParseError<AuthSignUpInputType> | undefined => {
	const zodErrors = authSignUpInputZodSchema.safeParse(DEFAULT_VALUES);
	return zodErrors.success ? undefined : zodErrors;
};

export const useSignUpForm = () => {
	const [signUpForm, setSignUpForm] = useState<AuthSignUpInputType>(DEFAULT_VALUES);

	const [validationErrors, setValidationErrors] = useState<SafeParseError<AuthSignUpInputType> | undefined>(
		getInitialValidationErrors,
	);

	const errors = validationErrors?.error.formErrors.fieldErrors;

	const newPassword = useNewPasswordError({
		password: signUpForm.password,
		email: signUpForm.email,
		formatErrorMessage: errors?.password?.at(0),
	});

	const disabled =
		signUpForm.email === "" ||
		signUpForm.password === "" ||
		signUpForm.companyName === "" ||
		signUpForm.personInChargeName === "" ||
		signUpForm.tel === "" ||
		signUpForm.howDoYouKnow === "" ||
		!signUpForm.agree ||
		validationErrors !== undefined ||
		!newPassword.isSubmittable;

	const onChangeValidationErrors = useCallback((errors: SafeParseError<AuthSignUpInputType> | undefined) => {
		setValidationErrors(errors);
	}, []);

	const onChangeSignUpForm = useCallback(
		<K extends keyof AuthSignUpInputType>(key: K, value: AuthSignUpInputType[K]) => {
			setSignUpForm((prev) => {
				const zodErrors = authSignUpInputZodSchema.safeParse({ ...prev, [key]: value });
				onChangeValidationErrors(zodErrors.success ? undefined : zodErrors);

				return {
					...prev,
					[key]: value,
				};
			});
		},
		[onChangeValidationErrors],
	);

	const onInitSignUpForm = useCallback(() => {
		setSignUpForm(DEFAULT_VALUES);
		onChangeValidationErrors(getInitialValidationErrors());
	}, [onChangeValidationErrors]);

	return {
		signUpForm,
		errors,
		passwordErrorMessage: newPassword.errorMessage,
		disabled,

		onChangeSignUpForm,
		onInitSignUpForm,
	};
};
